import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import { GroupWithRelationsDto, ResponseDto } from "@mimber/schema"
import { groupsKeys } from "../groups.utils"

interface UploadLogoVariables {
  file: File
}

interface UploadGroupLogoOptions extends Omit<
  UseMutationOptions<
    ResponseDto<GroupWithRelationsDto>,
    Error,
    UploadLogoVariables
  >,
  "mutationFn"
> {}

/**
 * Hook for uploading group logo
 *
 * Uploads a logo file to S3 and updates the group's logoUrl.
 * Automatically generates thumbnail via Lambda function.
 *
 * @param groupUuid - Group UUID
 * @param options - Additional options for the mutation
 * @returns Mutation hook for uploading logo
 *
 * @example
 * ```tsx
 * const uploadLogo = useUploadGroupLogo(groupUuid, {
 *   onSuccess: () => {
 *     toast.success('Logo uploaded successfully!')
 *   }
 * })
 *
 * // In file input onChange handler
 * const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 *   const file = e.target.files?.[0]
 *   if (file) {
 *     uploadLogo.mutate({ file })
 *   }
 * }
 * ```
 */
export const useUploadGroupLogo = (
  groupUuid: string,
  options: UploadGroupLogoOptions = {}
): UseMutationResult<
  ResponseDto<GroupWithRelationsDto>,
  Error,
  UploadLogoVariables
> => {
  guardAppUsage("mimber-organization", "useUploadGroupLogo")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ file }: UploadLogoVariables) => {
      // Create FormData for file upload
      const formData = new FormData()
      formData.append("file", file)

      // Upload logo
      const response = await server.post<ResponseDto<GroupWithRelationsDto>>(
        `groups/${groupUuid}/logo`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )

      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      // Invalidate queries to refetch with new logo URL
      queryClient.invalidateQueries({
        queryKey: groupsKeys.findAll(),
      })
      queryClient.invalidateQueries({
        queryKey: groupsKeys.findOne(groupUuid),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
